import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { SearchValueType, TypeOrArray } from '../../common-interfaces';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import { isDropDownNoSelection } from '../../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../../fields/drop-down/drop-down-types';
import DropDown from '../../fields/drop-down/drop-down.component';
import { hasColumnChanged } from '../data-grid-functions';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import type { DataGridFilterProps } from './data-grid-filter.interface';
import { OverlayScrollDetectionType } from '../../common/overlay-panel/overlay-panel.props';

export default function DataGridSingleDropDownFilter({
  search,
  column,
  filterSignal,
  scrollDetection = OverlayScrollDetectionType.Parent,
  forceResetColumnMap,
}: DataGridFilterProps<DropDownResult>) {
  const [currentValue, setCurrentValue] = useState<DropDownResult | undefined>(
    column.searchValue ? (column.searchValue as DropDownResult) : undefined
  );
  const previousColumn = useRef<DataGridColumnSettings>();

  const tagCreator = useTestTag(column.title || '');

  useEffect(() => {
    if (hasColumnChanged(forceResetColumnMap, column, previousColumn.current)) {
      setCurrentValue(column.searchValue ? (column.searchValue as DropDownResult) : undefined);
    }

    previousColumn.current = column;
  }, [column, forceResetColumnMap]);

  useEffect(() => {
    if (currentValue) {
      const clonedFilters = cloneDeep(filterSignal.value);
      const updatedFilters = clonedFilters.filter((filter) => filter.field !== column.field);
      updatedFilters.push({
        scrollerId: column.id,
        field: column.field,
        customDataProperties: column.customDataProperties,
        value: Array.isArray(currentValue.value)
          ? (currentValue.value as Array<SearchValueType>)
          : (currentValue.value as SearchValueType),
        text: currentValue.text,
        comparison: ContinuousScrollerComparisonType.Equals,
      });
      filterSignal.value = updatedFilters;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only effect to initialize filter from initial column values
  }, []);

  const onSelectionChange = (value?: TypeOrArray<DropDownResult> | DropDownNoSelection) => {
    if (value && !isDropDownNoSelection(value) && !Array.isArray(value)) {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);
      const clonedFilters = cloneDeep(filterSignal.value);
      if (filterIndex > -1) {
        clonedFilters.splice(filterIndex, 1, {
          scrollerId: column.id,
          field: column.field,
          customDataProperties: column.customDataProperties,
          value: Array.isArray(value.value)
            ? (value.value as Array<SearchValueType>)
            : (value.value as SearchValueType),
          text: value.text,
          comparison: ContinuousScrollerComparisonType.Equals,
        });
        filterSignal.value = clonedFilters;
      } else {
        clonedFilters.push({
          scrollerId: column.id,
          field: column.field,
          customDataProperties: column.customDataProperties,
          value: Array.isArray(value.value)
            ? (value.value as Array<SearchValueType>)
            : (value.value as SearchValueType),
          text: value.text,
          comparison: ContinuousScrollerComparisonType.Equals,
        });
        filterSignal.value = clonedFilters;
      }

      setCurrentValue(value);
    } else {
      if (filterSignal.value.findIndex((filter) => filter.scrollerId === column.id) > -1) {
        filterSignal.value = filterSignal.value.filter((filter) => filter.scrollerId !== column.id);
      }
      setCurrentValue(undefined);
    }
  };

  if (!column.dropDownText || !column.dropDownValue) {
    throw new Error('The dropDownText and dropDownValue properties must be set to use the drop down filter');
  }

  const dropDownData = useCallback(
    (scrollerSubscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (search && !Array.isArray(search)) {
        const scrollerOptions: OdinDataRetrievalOptions = {
          ...options,
          customProperties: column.customDataProperties,
        };

        search.getData(scrollerSubscriber, scrollerOptions);
      }
    },
    [search, column.customDataProperties]
  );

  const dataRetriever: OdinDataRetrieval = useMemo(() => {
    if (search && !Array.isArray(search) && search.getSubscriber) {
      return {
        getData: dropDownData,
        getSubscriber: search.getSubscriber,
      };
    }

    return {
      getData: dropDownData,
    };
  }, [search, dropDownData]);

  return (
    <div className="odin-w-full" ref={tagCreator('single-drop-down-filter')}>
      <DropDown
        id={column.id}
        textField={column.dropDownText}
        valueField={column.dropDownValue}
        value={currentValue}
        data={dataRetriever}
        allowClear={false}
        allowSearch={true}
        scrollDetection={scrollDetection}
        onChange={onSelectionChange}
      />
    </div>
  );
}
